import { Button, Notice, Stack, Switch, TextField } from '@eleveo/components-react';
import { CrossIcon } from '@eleveo/icons-react';
import React, { useCallback } from 'react';
import { useField, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useIntegrations } from '../../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../../constants/dataTestIds';
import {
  HISTORICAL_DATA_FIELDS,
  HistoricalDataImportTaskType,
  USER_SYNCHRONIZATION_FIELDS,
} from '../../../../scene/integrations/types';
import { ETL_INTEGRATION_TYPE } from '../../../configuration/types';

export const LOOK_BACK_MIN_VALUE = 1;
export const LOOK_BACK_MAX_VALUE = 12;

export const HistoricalData: React.FC = () => {
  const { isInReadOnlyMode, integrationType } = useIntegrations();
  const { t } = useTranslation();
  const { batch, change, resetFieldState } = useForm();

  const {
    input: { value: isUserSynchronizationEnabled },
  } = useField(USER_SYNCHRONIZATION_FIELDS.scheduled);
  const isDisabled = isInReadOnlyMode || !isUserSynchronizationEnabled;

  const { input: scheduledInput, meta: scheduledMeta } = useField(HISTORICAL_DATA_FIELDS.scheduled);
  const { input: cronInput, meta: cronMeta } = useField<HistoricalDataImportTaskType['cronExpression']>(
    HISTORICAL_DATA_FIELDS.cronExpression
  );
  const { input: lookBackInput, meta: lookBackMeta } = useField<
    NonNullable<HistoricalDataImportTaskType['taskProperties']>['lookBackPeriod']
  >(HISTORICAL_DATA_FIELDS.lookBackPeriod);

  const handleReset = useCallback(() => {
    batch(() => {
      change(HISTORICAL_DATA_FIELDS.scheduled, false);
      resetFieldState(HISTORICAL_DATA_FIELDS.scheduled);

      change(HISTORICAL_DATA_FIELDS.cronExpression, '');
      resetFieldState(HISTORICAL_DATA_FIELDS.cronExpression);

      change(HISTORICAL_DATA_FIELDS.lookBackPeriod, undefined);
      resetFieldState(HISTORICAL_DATA_FIELDS.lookBackPeriod);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLookBackPeriodChange = useCallback(
    (value: string) => {
      const newValue = Number(value);

      if (value === '') {
        lookBackInput.onChange('');
        return;
      }

      if (Number.isNaN(newValue)) {
        // filter out invalid characters
        return;
      }

      lookBackInput.onChange(value);
    },
    [lookBackInput]
  );

  return (
    <Stack direction="vertical" className="p-20 w-full" spacing={20}>
      <Switch
        className="w-full"
        {...scheduledInput}
        isSelected={scheduledInput.value}
        isDisabled={isDisabled}
        testId={TEST_IDS.checkboxHistoricalDataEnabled}
      >
        {t(`myConfigurations.tasks.${scheduledInput.value ? 'on' : 'off'}`)}
      </Switch>
      <TextField
        isRequired={scheduledInput.value}
        isReadOnly={isDisabled}
        isDisabled={!isUserSynchronizationEnabled}
        label={t('myConfigurations.tasks.cronExpression')}
        className="w-full task-configuration__cron-expression"
        validationState={cronMeta.invalid ? 'invalid' : 'valid'}
        errorMessage={t(cronMeta.error)}
        {...cronInput}
        testId={TEST_IDS.inputCronExpressionHistoricalData}
      />
      <TextField
        isRequired={scheduledInput.value}
        isReadOnly={isInReadOnlyMode}
        isDisabled={!isUserSynchronizationEnabled}
        label={t('myConfigurations.tasks.lookBackPeriod')}
        className="w-full"
        validationState={lookBackMeta.invalid ? 'invalid' : 'valid'}
        errorMessage={t(lookBackMeta.error, { min: LOOK_BACK_MIN_VALUE, max: LOOK_BACK_MAX_VALUE })}
        testId={TEST_IDS.inputLookBackPeriodHistoricalData}
        {...lookBackInput}
        value={lookBackInput.value ?? ''}
        onChange={handleLookBackPeriodChange}
      />
      <Notice className="mlb-0" testId={TEST_IDS.noticeHistoricalLookBackWarning}>
        {t(`myConfigurations.tasks.${integrationType === ETL_INTEGRATION_TYPE.wxcc ?
          'webexCCHistoricalDatalookBackPeriodDescription' :
          'lookBackPeriodDescription'}`,
          { max: LOOK_BACK_MAX_VALUE })}
      </Notice>

      {/* TODO: should be re-enabled in future */}
      {(scheduledMeta.dirty || !!cronInput.value) && false && (
        <Button
          color="warning"
          variant="quiet"
          onClick={() => handleReset()}
          className="w-min"
          isDisabled={isInReadOnlyMode}
        >
          <CrossIcon /> {t('myConfigurations.tasks.clear')}
        </Button>
      )}
    </Stack>
  );
};
